/**
 * Toolbox ▸ SDMS Template Builder — the template LIST (the front view, standing
 * rule 10). New / Edit / double-click open the full-page editor.
 *
 * Port of the Alpaca Studio SDMS Template Builder. Templates live in the shared
 * legacy SpreadSheetBuilder_* tables, so every template here is the one SDMS Result
 * Entry (and legacy ALPACA) runs — see SdmsTemplateBuilderController.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppToolbar } from '../../../components/AppToolbar';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { FormTitle } from '../../../components/FormTitle';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { useNavPermission } from '../../../hooks/useNavPermission';
import { errorText, sdmsTemplateApi, type Lookups, type TemplateListItem } from './sdmsTemplateApi';
import SdmsTemplateEditor from './SdmsTemplateEditor';
import { FORM_NAME, MODELS, PAGE_ID } from './templateConstants';
import './sdmsTemplateBuilder.css';

const modelLabel = (m: number) => MODELS.find(x => x.id === m)?.label.replace(/ \(.*\)$/, '') ?? String(m);

const COLUMNS: AppColumn<TemplateListItem>[] = [
  { field: 'templateID', header: 'Template ID', type: 'number', width: 100 },
  { field: 'templateName', header: 'Template Name', width: 240 },
  { field: 'templateTypeName', header: 'Type', width: 120 },
  { field: 'tests', header: 'Tests', width: 200 },
  { field: 'matrix', header: 'Matrix', width: 110 },
  { field: 'method', header: 'Method', width: 130 },
  { field: 'model', header: 'Data Model', width: 150, text: r => modelLabel(r.model), value: r => modelLabel(r.model) },
  { field: 'mappedCells', header: 'Mapped Cells', type: 'number', width: 110 },
  { field: 'status', header: 'Status', type: 'status', width: 100,
    value: r => (r.isRetire ? 'Retired' : r.isActive ? 'Active' : 'Inactive'), text: r => (r.isRetire ? 'Retired' : r.isActive ? 'Active' : 'Inactive') },
  { field: 'inUse', header: 'In Use', width: 80, text: r => (r.inUse ? 'Yes' : 'No'), value: r => (r.inUse ? 'Yes' : 'No') },
  { field: 'modifiedByName', header: 'Modified By', width: 140 },
  { field: 'modifiedDate', header: 'Modified Date', type: 'datetime', width: 150 },
];

export default function SdmsTemplateBuilderPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const runDelete = useDeleteFlow();
  const perm = useNavPermission('SDMS');

  const [rows, setRows] = useState<TemplateListItem[]>([]);
  const [lookups, setLookups] = useState<Lookups | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  /** null = list; 0 = new template; n = editing template n */
  const [editing, setEditing] = useState<number | null>(null);

  const load = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      setRows(await sdmsTemplateApi.list());
      setLoadMs(Math.round(performance.now() - t0));
    } catch (e) {
      notify.error(errorText(e, 'Could not load the templates.'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    sdmsTemplateApi.lookups().then(setLookups).catch(e => notify.error(errorText(e, 'Could not load the field catalog.')));
  }, [notify]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => `${r.templateID} ${r.templateName} ${r.templateTypeName} ${r.tests} ${r.matrix} ${r.method}`.toLowerCase().includes(q));
  }, [rows, search]);

  const selectedRow = () => {
    const ids = [...selected];
    if (ids.length !== 1) {
      void alert({ title: FORM_NAME, message: 'Select one template.', tone: 'warning' });
      return null;
    }
    return rows.find(r => r.templateID === ids[0]) ?? null;
  };

  const remove = async () => {
    const row = selectedRow();
    if (!row) return;
    if (row.inUse) {
      await alert({ title: FORM_NAME, message: `"${row.templateName}" cannot be deleted because data has already been entered with it. Retire it instead.`, tone: 'warning' });
      return;
    }
    await runDelete({
      formName: FORM_NAME,
      message: `Delete the SDMS template "${row.templateName}"? Its test links, cell mapping and report columns are deleted with it.`,
      delete: async () => { await sdmsTemplateApi.remove(row.templateID); },
      onError: e => notify.error(errorText(e, 'Delete failed.')),
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  };

  if (editing !== null) {
    return (
      <SdmsTemplateEditor
        key={editing}
        id={editing || null}
        lookups={lookups}
        onHome={onHome}
        onClose={() => { setEditing(null); void load(); }}
        onSaved={() => void load()}
        onOpen={id => setEditing(id)}
      />
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={onHome}>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Toolbox</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">SDMS Template Builder</span>
        </div>
        <FormTitle pageId={PAGE_ID} auditFormName={FORM_NAME} loadMs={loadMs} loading={loading} />
        <div className="dp-subtitle">{filtered.length} template{filtered.length === 1 ? '' : 's'}</div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onNew={perm.canWrite ? () => setEditing(0) : undefined}
            onEdit={() => { const r = selectedRow(); if (r) setEditing(r.templateID); }}
            onDelete={perm.canDelete ? () => void remove() : undefined}
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{ filename: 'sdms-templates', ...gridExportMatrix(filtered, COLUMNS) }}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search template, type, test, matrix…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<TemplateListItem>
            rows={filtered} columns={COLUMNS} getRowId={r => r.templateID} loading={loading}
            selectable selectedIds={selected} onSelectionChange={setSelected}
            onRowDoubleClick={r => setEditing(r.templateID)}
            initialSort={{ key: 'templateName', dir: 'asc' }}
            emptyText="No SDMS templates yet." />
        </div>
      </div>
    </div>
  );
}
